/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { Input } from '../Input/index.js';
import { useSafeContext, useMergedRefs, useContainerWidth, mergeEventHandlers, } from '../utils/index.js';
import { ComboBoxMultipleContainer } from './ComboBoxMultipleContainer.js';
import { ComboBoxStateContext, ComboBoxActionContext, ComboBoxRefsContext, } from './helpers.js';
export const ComboBoxInput = React.forwardRef((props, forwardedRef) => {
    const { onKeyDown: onKeyDownProp, onFocus: onFocusProp, onClick: onClickProp, selectTags, ...rest } = props;
    const { isOpen, id, focusedIndex, enableVirtualization, multiple, onClickHandler, } = useSafeContext(ComboBoxStateContext);
    const dispatch = useSafeContext(ComboBoxActionContext);
    const { inputRef, menuRef, optionsExtraInfoRef } = useSafeContext(ComboBoxRefsContext);
    const refs = useMergedRefs(inputRef, forwardedRef);
    const focusedIndexRef = React.useRef(focusedIndex !== null && focusedIndex !== void 0 ? focusedIndex : -1);
    React.useEffect(() => {
        focusedIndexRef.current = focusedIndex !== null && focusedIndex !== void 0 ? focusedIndex : -1;
    }, [focusedIndex]);
    const getIdFromIndex = (index) => {
        var _a, _b, _c;
        return ((_c = (_b = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-iui-index="${index}"]`)) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : '');
    };
    const handleKeyDown = React.useCallback((event) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        onKeyDownProp === null || onKeyDownProp === void 0 ? void 0 : onKeyDownProp(event);
        const length = (_a = Object.keys(optionsExtraInfoRef.current).length) !== null && _a !== void 0 ? _a : 0;
        if (event.altKey) {
            return;
        }
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                if (!isOpen) {
                    return dispatch({ type: 'open' });
                }
                if (length === 0) {
                    return;
                }
                if (focusedIndexRef.current === -1) {
                    const currentElement = (_b = menuRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('[data-iui-index]');
                    return dispatch({
                        type: 'focus',
                        value: Number((_c = currentElement === null || currentElement === void 0 ? void 0 : currentElement.getAttribute('data-iui-index')) !== null && _c !== void 0 ? _c : 0),
                    });
                }
                // If virtualization is enabled, dont let round scrolling
                if (enableVirtualization &&
                    !((_e = (_d = menuRef.current) === null || _d === void 0 ? void 0 : _d.querySelector(`[data-iui-index="${focusedIndexRef.current}"]`)) === null || _e === void 0 ? void 0 : _e.nextElementSibling)) {
                    return;
                }
                let nextIndex = focusedIndexRef.current;
                do {
                    const currentElement = (_f = menuRef.current) === null || _f === void 0 ? void 0 : _f.querySelector(`[data-iui-index="${nextIndex}"]`);
                    const nextElement = (_g = currentElement === null || currentElement === void 0 ? void 0 : currentElement.nextElementSibling) !== null && _g !== void 0 ? _g : (_h = menuRef.current) === null || _h === void 0 ? void 0 : _h.querySelector('[data-iui-index]');
                    nextIndex = Number(nextElement === null || nextElement === void 0 ? void 0 : nextElement.getAttribute('data-iui-index'));
                    if (nextElement) {
                        return dispatch({ type: 'focus', value: nextIndex });
                    }
                } while (nextIndex !== focusedIndexRef.current);
                break;
            }
            case 'ArrowUp': {
                event.preventDefault();
                if (!isOpen) {
                    return dispatch({ type: 'open' });
                }
                if (length === 0) {
                    return;
                }
                // If virtualization is enabled, dont let round scrolling
                if (enableVirtualization &&
                    !((_k = (_j = menuRef.current) === null || _j === void 0 ? void 0 : _j.querySelector(`[data-iui-index="${focusedIndexRef.current}"]`)) === null || _k === void 0 ? void 0 : _k.previousElementSibling)) {
                    return;
                }
                if (focusedIndexRef.current === -1) {
                    return dispatch({
                        type: 'focus',
                        value: (_m = (_l = Object.values(optionsExtraInfoRef.current)) === null || _l === void 0 ? void 0 : _l[length - 1].__originalIndex) !== null && _m !== void 0 ? _m : -1,
                    });
                }
                let prevIndex = focusedIndexRef.current;
                do {
                    const currentElement = (_o = menuRef.current) === null || _o === void 0 ? void 0 : _o.querySelector(`[data-iui-index="${prevIndex}"]`);
                    const prevElement = (_p = currentElement === null || currentElement === void 0 ? void 0 : currentElement.previousElementSibling) !== null && _p !== void 0 ? _p : (_q = menuRef.current) === null || _q === void 0 ? void 0 : _q.querySelector('[data-iui-index]:last-of-type');
                    prevIndex = Number(prevElement === null || prevElement === void 0 ? void 0 : prevElement.getAttribute('data-iui-index'));
                    if (prevElement) {
                        return dispatch({ type: 'focus', value: prevIndex });
                    }
                } while (prevIndex !== focusedIndexRef.current);
                break;
            }
            case 'Enter': {
                event.preventDefault();
                if (isOpen) {
                    if (focusedIndexRef.current > -1) {
                        onClickHandler === null || onClickHandler === void 0 ? void 0 : onClickHandler(focusedIndexRef.current);
                    }
                }
                else {
                    dispatch({ type: 'open' });
                }
                break;
            }
            case 'Escape': {
                event.preventDefault();
                dispatch({ type: 'close' });
                break;
            }
            case 'Tab':
                dispatch({ type: 'close' });
                break;
        }
    }, [
        dispatch,
        enableVirtualization,
        isOpen,
        menuRef,
        onClickHandler,
        onKeyDownProp,
        optionsExtraInfoRef,
    ]);
    const handleFocus = React.useCallback((event) => {
        dispatch({ type: 'open' });
        onFocusProp === null || onFocusProp === void 0 ? void 0 : onFocusProp(event);
    }, [dispatch, onFocusProp]);
    const handleClick = React.useCallback(() => {
        if (!isOpen) {
            dispatch({ type: 'open' });
        }
    }, [dispatch, isOpen]);
    const [tagContainerWidthRef, tagContainerWidth] = useContainerWidth();
    return (React.createElement(React.Fragment, null,
        React.createElement(Input, { ref: refs, onKeyDown: handleKeyDown, onClick: mergeEventHandlers(onClickProp, handleClick), onFocus: handleFocus, "aria-activedescendant": isOpen && focusedIndex != undefined && focusedIndex > -1
                ? getIdFromIndex(focusedIndex)
                : undefined, role: 'combobox', "aria-controls": isOpen ? `${id}-list` : undefined, "aria-autocomplete": 'list', spellCheck: false, autoCapitalize: 'none', autoCorrect: 'off', style: multiple ? { paddingLeft: tagContainerWidth + 18 } : {}, "aria-describedby": multiple ? `${id}-selected-live` : undefined, ...rest }),
        multiple && selectTags ? (React.createElement(ComboBoxMultipleContainer, { ref: tagContainerWidthRef, selectedItems: selectTags, id: `${id}-selected-live` })) : null));
});
ComboBoxInput.displayName = 'ComboBoxInput';
